import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ALERT_TYPE } from 'src/app/constants';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CompanyAccess } from 'src/model/companyAccess.model';
import { AccessType, AdminAccessService } from 'src/services/admin-access.service';
import { AccessAction, AccessActionComponent } from '../access-action/access-action.component';

@Component({
    selector: 'app-admin-company-list',
    templateUrl: './admin-company-list.component.html',
    styleUrl: './admin-company-list.component.scss',
    standalone: false
})
export class AdminCompanyListComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  protected sharedService = inject(SharedService);
  protected enums = inject(Enums);
  protected adminAccessService = inject(AdminAccessService);


  @Input() companyAccessList: CompanyAccess[] = [];
  @Input() currentAdmin: string;
  @Input() companyCode: string;

  readonly NORMAL_ACCESS = 'Normal Access';
  readonly TEMPORARY_ACCESS = 'Temporary Access';
  readonly ONDUTY_ACCESS = 'On Duty Access';

  normalAccessList: CompanyAccess[] = [];
  temporaryAccessList: CompanyAccess[] = [];
  onDutyAccessList: CompanyAccess[] = [];
  accessMap: { key: string; access: CompanyAccess[]; isCollapsed: boolean; }[] = [];

  ngOnInit(): void {
    if (this.companyAccessList != undefined && this.companyAccessList.length > 0) {
      this.companyAccessList.sort((a, b) =>
        a.Priority - b.Priority ||
        a.Username.localeCompare(b.Username) ||
        a.CompanyCode.localeCompare(b.CompanyCode)
      );

      [this.normalAccessList, this.temporaryAccessList, this.onDutyAccessList] = this.companyAccessList.reduce(
        ([normal, temporary, onDuty], o) => {
          switch (this.adminAccessService.getAccessType(o.Priority)) {
            case AccessType.Normal:
              normal.push(o);
              break;
            case AccessType.Temporary:
              temporary.push(o);
              break;
            case AccessType.OnDuty:
              onDuty.push(o);
              break;
          }
          return [normal, temporary, onDuty];
        },
        [[], [], []]
      );

      this.accessMap = [
        { key: this.NORMAL_ACCESS, access: this.normalAccessList, isCollapsed: false },
        { key: this.TEMPORARY_ACCESS, access: this.temporaryAccessList, isCollapsed: false },
        { key: this.ONDUTY_ACCESS, access: this.onDutyAccessList, isCollapsed: true }
      ].filter(o => o.access.length > 0);
    }
  }

  toggleCollapse(key: string) {
    const entry = this.accessMap.find(a => a.key === key);
    if (entry)
      entry.isCollapsed = !entry.isCollapsed;
  }

  reset() {
    this.activeModal.close();
  }

  openChangeAccessModal(access: CompanyAccess) {
    const modalRef = this.sharedService.openModal(AccessActionComponent);
    modalRef.componentInstance.action = AccessAction.Change;
    modalRef.componentInstance.companyAccessList = this.companyAccessList.filter(c => c.CompanyId === access.CompanyId);
    modalRef.componentInstance.currentAdmin = access.Username;
    modalRef.componentInstance.targetPriority = access.Priority;
  }

  openRevokePriorityModal(access: CompanyAccess) {
    const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
    modalRef.componentInstance.message = "Are you sure you want to revoke access?";

    modalRef.result.then(async (confirm) => {
      if (confirm) {
        const adminName = (this.currentAdmin != undefined) ? this.currentAdmin : access.Username;
        const revoked = await this.adminAccessService.revokeAccess(adminName, [access.CompanyId]);
        if (revoked.companyIds.length > 0) window.location.reload();
      }
    });
  }

  openRevokeTemporaryAccessModal() {
    const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
    modalRef.componentInstance.message = "Are you sure you want to revoke all temporary access?";

    modalRef.result.then(async (confirm) => {
      if (confirm) {
        let revoked: number[] = [];
        if (this.currentAdmin !== undefined) {
          revoked = (await this.adminAccessService.revokeAccess(this.currentAdmin, this.temporaryAccessList.map(access => access.CompanyId))).companyIds;
        } else {
          const req = this.temporaryAccessList.map(a => this.adminAccessService.revokeAccess(a.Username, [a.CompanyId]));
          revoked = (await Promise.all(req)).map(r => r.companyIds).flat();
        }

        if (revoked.length === this.temporaryAccessList.length) {
          this.sharedService.openAlert(`All ${revoked.length} temporary access revoked successfully!`, ALERT_TYPE.SUCCESS, true);
        } else {
          this.sharedService.openAlert(`${revoked.length} of ${this.temporaryAccessList.length} temporary access revoked successfully!`, ALERT_TYPE.DANGER, true, {auto: false});
        }
      }
    });
  }
}
